import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UsageEventsApi } from '../../services/ApiService';
import { GenericUsageEventMetricsApi } from '../../services/api/GenericUsageEventMetricsApi';
import { useAuth } from '../../contexts/AuthContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MetricCard } from '../../components/analytics/SharedAnalyticsComponents';

/** Utility */
const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];
const defaultRangeDays = 7;

/**
 * UsageEventsDashboard
 * Initial version: focuses on per-user summary (requires userId), optional app-wide summary (admin), and recent events for a selected eventType.
 * Future enhancements once backend endpoints exist:
 *  - aggregated timeline series (counts over time)
 *  - filters by sessionId, correlationId, category, action, entityType
 *  - global distribution of event types
 */
export default function UsageEventsDashboard() {
  const navigate = useNavigate();
  const { user, isInitialized } = useAuth();
  const [filters, setFilters] = useState(() => {
    const to = new Date();
    const from = new Date(Date.now() - defaultRangeDays * 86400000);
    return { from: from.toISOString(), to: to.toISOString(), userId: '', eventType: '', sessionId:'', correlationId:'', bucket:'auto', page:0, size:50, scope:'auto' };
  });
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [appSummary, setAppSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  // NEW aggregated data
  const [topTypes, setTopTypes] = useState([]); // from top-types endpoint
  const [timeSeries, setTimeSeries] = useState([]); // from time-series endpoint
  const [activeSessions, setActiveSessions] = useState([]); // from active-sessions
  const [landingPageSessions, setLandingPageSessions] = useState([]); // from active-sessions with anonymousOnly=true
  const [usageStats, setUsageStats] = useState(null); // from usage-stats (object)
  const [searchPage, setSearchPage] = useState(null); // paginated search results (admin/user search)
  // NEW: recent users activity (latest events across users)
  const [recentUsersPage, setRecentUsersPage] = useState(null); // Admin: recent user events
  const [recentUsersPageIndex, setRecentUsersPageIndex] = useState(0);
  const [recentUsersLoading, setRecentUsersLoading] = useState(false);
  const [recentUsersError, setRecentUsersError] = useState(null);
  
  // NEW: Enhanced metrics from GenericUsageEventMetricsApi
  const [metricsView, setMetricsView] = useState('details'); // 'overview' or 'details' - default to Enhanced Metrics
  const [metricsData, setMetricsData] = useState({
    eventTypeDistribution: [],
    topUsers: [],
    volumeSeries: [],
    featureCategoryDistribution: [],
    summaryStats: null,
    topFeatures: []
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  const loadData = useCallback(async () => {
  if (!isInitialized) return;
    setLoading(true); setError(null);
    try {
      const scopeIsAdmin = user?.roles?.some(r => r.roleName === 'admin');
      const effectiveScope = filters.scope === 'auto' ? (scopeIsAdmin ? 'admin' : 'user') : filters.scope; // 'admin' or 'user'
      const bucket = filters.bucket === 'auto' ? ((new Date(filters.to) - new Date(filters.from)) <= 48*3600*1000 ? 'hour' : 'day') : filters.bucket;

      // Build promises object - conditionally include user-specific calls only if userId is provided
      const promises = {};
      
      // User-specific endpoints (require userId)
      if (filters.userId) {
        promises.userSummaryRes = UsageEventsApi.getUserSummary({ userId: filters.userId, from: filters.from, to: filters.to });
        if (filters.eventType) {
          promises.recentRes = UsageEventsApi.getUserRecentByType({ userId: filters.userId, eventType: filters.eventType });
        }
      }
      
      // Admin or filtered endpoints (work with or without userId)
      promises.topTypesRes = (effectiveScope === 'admin' ? UsageEventsApi.adminTopTypes({ userId: filters.userId || undefined, from: filters.from, to: filters.to, limit: 15 }) : (filters.userId ? UsageEventsApi.userTopTypes({ userId: filters.userId, from: filters.from, to: filters.to, limit: 15 }) : null));
      promises.timeSeriesRes = (effectiveScope === 'admin' ? UsageEventsApi.adminTimeSeries({ userId: filters.userId || undefined, eventType: filters.eventType || undefined, from: filters.from, to: filters.to, bucket }) : (filters.userId ? UsageEventsApi.userTimeSeries({ userId: filters.userId, eventType: filters.eventType || undefined, from: filters.from, to: filters.to, bucket }) : null));
      promises.activeSessionsRes = (effectiveScope === 'admin' ? UsageEventsApi.adminActiveSessions({ userId: filters.userId || undefined, from: filters.from, to: filters.to, limit: 20 }) : (filters.userId ? UsageEventsApi.userActiveSessions({ userId: filters.userId, from: filters.from, to: filters.to, limit: 20 }) : null));
      promises.landingPageSessionsRes = (effectiveScope === 'admin' ? UsageEventsApi.adminActiveSessions({ anonymousOnly: true, from: filters.from, to: filters.to, limit: 20 }) : null);
      promises.usageStatsRes = (effectiveScope === 'admin' ? UsageEventsApi.adminUsageStats({ userId: filters.userId || undefined, from: filters.from, to: filters.to }) : (filters.userId ? UsageEventsApi.userUsageStats({ userId: filters.userId, from: filters.from, to: filters.to }) : null));
      promises.searchRes = (effectiveScope === 'admin' ? UsageEventsApi.adminSearch({ userId: filters.userId || undefined, eventType: filters.eventType || undefined, sessionId: filters.sessionId || undefined, correlationId: filters.correlationId || undefined, from: filters.from, to: filters.to, page: filters.page, size: filters.size }) : (filters.userId ? UsageEventsApi.userSearch({ userId: filters.userId, eventType: filters.eventType || undefined, sessionId: filters.sessionId || undefined, correlationId: filters.correlationId || undefined, from: filters.from, to: filters.to, page: filters.page, size: filters.size }) : null));
      
      const results = await Promise.all(Object.values(promises));
      const resultKeys = Object.keys(promises);
      const resultMap = {};
      resultKeys.forEach((key, index) => {
        resultMap[key] = results[index];
      });
      
      const { userSummaryRes, recentRes, topTypesRes, timeSeriesRes, activeSessionsRes, landingPageSessionsRes, usageStatsRes, searchRes } = resultMap;
      
      if (userSummaryRes?.ok) {
        try { setSummary(await userSummaryRes.json()); } catch { setSummary(null); }
      } else if (userSummaryRes) {
        setSummary(null);
      } else {
        setSummary(null);
      }
      if (recentRes?.ok) {
        try { setRecent(await recentRes.json()); } catch { setRecent([]); }
      } else {
        setRecent([]);
      }
      if (topTypesRes?.ok) { try { setTopTypes(await topTypesRes.json()); } catch { setTopTypes([]); } } else setTopTypes([]);
      if (timeSeriesRes?.ok) { try { setTimeSeries(await timeSeriesRes.json()); } catch { setTimeSeries([]); } } else setTimeSeries([]);
      if (activeSessionsRes?.ok) { try { setActiveSessions(await activeSessionsRes.json()); } catch { setActiveSessions([]); } } else setActiveSessions([]);
      if (landingPageSessionsRes?.ok) { try { setLandingPageSessions(await landingPageSessionsRes.json()); } catch { setLandingPageSessions([]); } } else setLandingPageSessions([]);
      if (usageStatsRes?.ok) { try { setUsageStats(await usageStatsRes.json()); } catch { setUsageStats(null); } } else setUsageStats(null);
      if (searchRes?.ok) { try { setSearchPage(await searchRes.json()); } catch { setSearchPage(null); } } else setSearchPage(null);
      // Attempt app summary if admin (may require elevated perms)
      if (user?.roles?.some(r => r.roleName === 'admin')) {
        try {
          const appRes = await UsageEventsApi.getAppSummary({ from: filters.from, to: filters.to });
          if (appRes?.ok) {
            setAppSummary(await appRes.json());
          }
        } catch {/* ignore */}
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filters, isInitialized, user]);

  // Separate effect for admin recent user events (not gated by userId filter)
  useEffect(() => {
    let ignore = false;
    const loadRecentUsers = async () => {
      if (!isInitialized) return;
      if (!user?.roles?.some(r => r.roleName === 'admin')) return;
      setRecentUsersLoading(true); setRecentUsersError(null);
      try {
        const resp = await UsageEventsApi.adminRecentUsers({ from: filters.from, to: filters.to, page: recentUsersPageIndex, size: 25 });
        if (resp?.ok) {
          let json = null; try { json = await resp.json(); } catch { json = null; }
          if (!ignore) setRecentUsersPage(json);
        } else if (!ignore) {
          setRecentUsersPage(null);
          setRecentUsersError(new Error(`Recent users fetch failed (${resp?.status})`));
        }
      } catch (e) {
        if (!ignore) {
          setRecentUsersError(e);
          setRecentUsersPage(null);
        }
      } finally {
        if (!ignore) setRecentUsersLoading(false);
      }
    };
    loadRecentUsers();
    return () => { ignore = true; };
  }, [isInitialized, user, filters.from, filters.to, recentUsersPageIndex]);

  // NEW: Load enhanced metrics from GenericUsageEventMetricsApi (admin only)
  useEffect(() => {
    let ignore = false;
    const loadEnhancedMetrics = async () => {
      if (!isInitialized) return;
      if (!user?.roles?.some(r => r.roleName === 'admin')) return;
      if (metricsView !== 'details') return; // Only load when viewing details
      
      setMetricsLoading(true);
      setMetricsError(null);
      try {
        const startDate = new Date(filters.from);
        const endDate = new Date(filters.to);
        
        const [
          eventTypeDistribution,
          topUsers,
          volumeSeries,
          featureCategoryDistribution,
          summaryStats,
          topFeatures
        ] = await Promise.all([
          GenericUsageEventMetricsApi.getEventTypeDistribution(startDate, endDate).then(r => r.ok ? r.json() : []).catch(() => []),
          GenericUsageEventMetricsApi.getTopUsersByEventCount(startDate, endDate, 15).then(r => r.ok ? r.json() : []).catch(() => []),
          GenericUsageEventMetricsApi.getEventVolumeSeries(startDate, endDate).then(r => r.ok ? r.json() : []).catch(() => []),
          GenericUsageEventMetricsApi.getFeatureCategoryDistribution(startDate, endDate).then(r => r.ok ? r.json() : []).catch(() => []),
          GenericUsageEventMetricsApi.getSummaryStats(startDate, endDate).then(r => r.ok ? r.json() : null).catch(() => null),
          GenericUsageEventMetricsApi.getTopFeaturesByUsage(startDate, endDate, 15).then(r => r.ok ? r.json() : []).catch(() => [])
        ]);
        
        if (!ignore) {
          setMetricsData({
            eventTypeDistribution,
            topUsers,
            volumeSeries,
            featureCategoryDistribution,
            summaryStats,
            topFeatures
          });
        }
      } catch (e) {
        if (!ignore) setMetricsError(e);
      } finally {
        if (!ignore) setMetricsLoading(false);
      }
    };
    loadEnhancedMetrics();
    return () => { ignore = true; };
  }, [isInitialized, user, filters.from, filters.to, metricsView]);

  useEffect(() => { loadData(); }, [loadData, refreshIndex]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const handleDateQuick = (days) => {
    const to = new Date();
    const from = new Date(Date.now() - days * 86400000);
    setFilters(f => ({ ...f, from: from.toISOString(), to: to.toISOString() }));
  };

  const clearFilters = () => { setFilters(f => ({ ...f, userId: '', eventType: '', sessionId:'', correlationId:'', page:0 })); setSearchPage(null); };

  const handlePageChange = (delta) => setFilters(f => ({ ...f, page: Math.max(0, (f.page||0) + delta) }));

  const reload = () => setRefreshIndex(i => i + 1);
  const recentUsersPrev = () => setRecentUsersPageIndex(i => Math.max(0, i - 1));
  const recentUsersNext = () => { if (!recentUsersPage?.last) setRecentUsersPageIndex(i => i + 1); };

  // Build chart-friendly structures from summary (expected shape based on example: array of { userId,eventType,count })
  const eventTypeDistribution = useMemo(() => {
    if (Array.isArray(topTypes) && topTypes.length) {
      return topTypes.map(t => ({ eventType: t.eventType || 'UNKNOWN', count: t.count || t.total || 0 }));
    }
    if (!summary || !Array.isArray(summary)) return [];
    return summary.map(s => ({ eventType: s.eventType || 'UNKNOWN', count: s.count || 0 }));
  }, [summary, topTypes]);

  const totalCount = useMemo(() => eventTypeDistribution.reduce((a,b) => a + b.count, 0), [eventTypeDistribution]);

  // Helper: render payload JSON as tokens if possible
  const renderPayloadTokens = (raw) => {
    if (raw == null) return <span style={{ fontSize:'.65rem', color:'#999' }}>—</span>;
    let obj = null;
    if (typeof raw === 'object') {
      obj = raw;
    } else if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try { obj = JSON.parse(trimmed); } catch { /* ignore parse failure */ }
      }
    }
    if (!obj) {
      // Fallback: show truncated string value
      const display = typeof raw === 'string' ? raw.slice(0,240) : String(raw).slice(0,240);
      return <span style={{ fontSize:'.65rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', display:'inline-block', maxWidth:240 }} title={typeof raw === 'string' ? raw : JSON.stringify(raw)}>{display}</span>;
    }
    // Normalize to array of [key,value] tokens. If array, use indices.
    const entries = Array.isArray(obj)
      ? obj.map((v,i)=>[i, v])
      : Object.entries(obj).slice(0,25); // limit tokens to avoid overflow
    return (
      <span style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem', maxWidth:300 }}>
        {entries.map(([k,v]) => {
          const valStr = typeof v === 'object' ? JSON.stringify(v) : String(v);
          const shortVal = valStr.length > 40 ? valStr.slice(0,37)+'…' : valStr;
          return (
            <span
              key={k}
              style={{
                background:'var(--color-bg-subtle)',
                border:'1px solid #d0d7de',
                borderRadius:'14px',
                padding:'2px 8px',
                fontSize:'.6rem',
                lineHeight:1.4,
                maxWidth:140,
                display:'inline-flex',
                flexDirection:'row',
                alignItems:'center'
              }}
              title={`${k}: ${valStr}`}
            >
              <span style={{ fontWeight:600, marginRight:4 }}>{k}:</span>
              <span style={{ color:'#333' }}>{shortVal || '∅'}</span>
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <div className="settings-container">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
        <h2 style={{ margin:0 }}>Generic Usage Events</h2>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          {user?.roles?.some(r => r.roleName === 'admin') && (
            <button 
              className={metricsView === 'details' ? 'primary' : 'secondary'} 
              onClick={() => setMetricsView(metricsView === 'overview' ? 'details' : 'overview')}
            >
              {metricsView === 'overview' ? '📊 Enhanced Metrics' : '📋 Event Search'}
            </button>
          )}
          <button onClick={reload} disabled={loading}>{loading ? 'Loading…' : '🔄 Refresh'}</button>
          <button className="secondary" onClick={() => handleDateQuick(1)}>24h</button>
          <button className="secondary" onClick={() => handleDateQuick(7)}>7d</button>
          <button className="secondary" onClick={() => handleDateQuick(30)}>30d</button>
          <button className="secondary" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      {/* Enhanced Metrics View (Admin Only) */}
      {metricsView === 'details' && user?.roles?.some(r => r.roleName === 'admin') && (
        <div style={{ marginTop:'1.5rem' }}>
          <h3 style={{ marginBottom:'1rem', fontSize:'1.1rem', fontWeight:600 }}>Enhanced Analytics</h3>
          
          {metricsLoading && <div>Loading enhanced metrics…</div>}
          {metricsError && <div style={{ color:'#c00' }}>Error loading metrics: {metricsError.message}</div>}
          
          {!metricsLoading && metricsData.summaryStats && (
            <div style={{ marginBottom:'1.5rem', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'1rem' }}>
              <MetricCard 
                label="Total Events" 
                value={metricsData.summaryStats.totalEvents?.toLocaleString() || '0'}
                subtitle={`across ${metricsData.summaryStats.uniqueSessions || 0} sessions`}
                sparklineData={(metricsData.volumeSeries || []).slice(-7).map(d => ({ value: d.count || 0 }))}
                trend={(metricsData.volumeSeries || []).length > 1 ? 
                  ((metricsData.volumeSeries[metricsData.volumeSeries.length-1]?.count - metricsData.volumeSeries[0]?.count) / (metricsData.volumeSeries[0]?.count || 1) * 100) : 0
                }
                color="#007bff"
              />
              <MetricCard 
                label="Unique Users" 
                value={metricsData.summaryStats.uniqueUsers?.toLocaleString() || '0'}
                subtitle={`${(metricsData.summaryStats.totalEvents / (metricsData.summaryStats.uniqueUsers || 1)).toFixed(0)} events/user`}
                color="#28a745"
              />
              <MetricCard 
                label="Event Types" 
                value={metricsData.summaryStats.uniqueEventTypes?.toLocaleString() || '0'}
                subtitle="distinct event types"
                color="#ffc107"
              />
              <MetricCard 
                label="Unique Sessions" 
                value={metricsData.summaryStats.uniqueSessions?.toLocaleString() || '0'}
                subtitle={`${(metricsData.summaryStats.totalEvents / (metricsData.summaryStats.uniqueSessions || 1)).toFixed(1)} events/session`}
                color="#17a2b8"
              />
            </div>
          )}
          
          {!metricsLoading && metricsData.volumeSeries?.length > 0 && (
            <div style={{ marginBottom:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
              <h4 style={{ marginTop:0, fontSize:'.95rem' }}>Event Volume Over Time</h4>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={metricsData.volumeSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString()} />
                  <YAxis />
                  <Tooltip labelFormatter={(v) => new Date(v).toLocaleString()} />
                  <Legend />
                  <Line type="monotone" dataKey="count" name="Events" stroke="#007bff" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(400px,1fr))', gap:'1.5rem', marginBottom:'1.5rem' }}>
            {!metricsLoading && metricsData.eventTypeDistribution?.length > 0 && (
              <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
                <h4 style={{ marginTop:0, fontSize:'.95rem' }}>Top Event Types</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={metricsData.eventTypeDistribution.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="eventType" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#28a745" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {!metricsLoading && metricsData.topFeatures?.length > 0 && (
              <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
                <h4 style={{ marginTop:0, fontSize:'.95rem' }}>Top Features Used</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={metricsData.topFeatures} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="featureName" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#ffc107" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(400px,1fr))', gap:'1.5rem', marginBottom:'1.5rem' }}>
            {!metricsLoading && metricsData.featureCategoryDistribution?.length > 0 && (
              <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
                <h4 style={{ marginTop:0, fontSize:'.95rem' }}>Feature Categories</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie 
                      data={metricsData.featureCategoryDistribution} 
                      dataKey="count" 
                      nameKey="featureCategory" 
                      outerRadius={90} 
                      label
                    >
                      {metricsData.featureCategoryDistribution.map((e, i) => (
                        <Cell key={e.featureCategory} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {!metricsLoading && metricsData.topUsers?.length > 0 && (
              <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
                <h4 style={{ marginTop:0, fontSize:'.95rem' }}>Most Active Users</h4>
                <div style={{ maxHeight:280, overflowY:'auto' }}>
                  <table style={{ width:'100%', fontSize:'.8rem' }}>
                    <thead>
                      <tr style={{ position:'sticky', top:0, background:'var(--card-bg)' }}>
                        <th style={{ textAlign:'left', padding:'.25rem' }}>User ID</th>
                        <th style={{ textAlign:'right', padding:'.25rem' }}>Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metricsData.topUsers.map(u => (
                        <tr key={u.userId}>
                          <td style={{ padding:'.25rem' }}>{u.userId}</td>
                          <td style={{ textAlign:'right', padding:'.25rem' }}>{u.count?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Original Event Search View */}
      {metricsView === 'overview' && (
        <>
          <div style={{ marginTop:'1rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
  <form onSubmit={e => { e.preventDefault(); loadData(); }} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'0.75rem' }}>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>From</label>
            <input name="from" value={filters.from} onChange={handleChange} />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>To</label>
            <input name="to" value={filters.to} onChange={handleChange} />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>User ID</label>
            <input name="userId" value={filters.userId} onChange={handleChange} placeholder="Required" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Event Type (recent)</label>
            <input name="eventType" value={filters.eventType} onChange={handleChange} placeholder="PAGE_VIEW" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Session ID</label>
            <input name="sessionId" value={filters.sessionId} onChange={handleChange} placeholder="session UUID" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Correlation ID</label>
            <input name="correlationId" value={filters.correlationId} onChange={handleChange} placeholder="corr UUID" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Bucket</label>
            <select name="bucket" value={filters.bucket} onChange={handleChange}>
              <option value="auto">auto</option>
              <option value="hour">hour</option>
              <option value="day">day</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Page Size</label>
            <select name="size" value={filters.size} onChange={handleChange}>
              {[25,50,100,250].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Scope</label>
            <select name="scope" value={filters.scope} onChange={handleChange}>
              <option value="auto">auto</option>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div style={{ display:'flex', alignItems:'flex-end' }}>
            <button type="submit" style={{ width:'100%' }}>Apply</button>
          </div>
        </form>
        {!filters.userId && <div style={{ marginTop:'0.75rem', fontSize:'.75rem', color:'#c00' }}>Enter a User ID to load summary.</div>}
      </div>

      {error && <div style={{ marginTop:'1rem', color:'#c00' }}>{error.message}</div>}

      {filters.userId && loading && <div style={{ marginTop:'1rem' }}>Loading events…</div>}

      {filters.userId && !loading && (
        <>
          <div style={{ marginTop:'1rem', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.75rem' }}>
            <StatCard label="Total Events" value={totalCount.toLocaleString()} />
            {usageStats?.distinctEventTypes != null && <StatCard label="Distinct Types" value={usageStats.distinctEventTypes.toLocaleString()} />}
            {usageStats?.distinctSessions != null && <StatCard label="Distinct Sessions" value={usageStats.distinctSessions.toLocaleString()} />}
            {usageStats?.distinctCorrelationIds != null && <StatCard label="Distinct Correlations" value={usageStats.distinctCorrelationIds.toLocaleString()} />}
            {recent.length > 0 && <StatCard label="Recent Events Loaded" value={recent.length.toString()} />}
            {appSummary && <StatCard label="App Total" value={(appSummary.totalCount||0).toLocaleString()} />}
          </div>

          {timeSeries.length > 0 && (
            <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
              <h3 style={{ marginTop:0 }}>Time Series</h3>
              <div style={{ width:'100%', height:300 }}>
                <ResponsiveContainer>
                  <LineChart data={timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={v=> (v||'').slice(0,19).replace('T',' ')} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" name="Count" stroke="#28a745" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {eventTypeDistribution.length > 0 && (
            <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
              <h3 style={{ marginTop:0 }}>Event Type Distribution</h3>
              <div style={{ width:'100%', height:320 }}>
                <ResponsiveContainer>
                  <BarChart data={eventTypeDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="eventType" hide={eventTypeDistribution.length > 10} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Count" fill="#007bff" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {eventTypeDistribution.length > 0 && (
            <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
              <h3 style={{ marginTop:0 }}>Event Types (Pie)</h3>
              <div style={{ width:'100%', height:320 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={eventTypeDistribution} dataKey="count" nameKey="eventType" outerRadius={110} label>
                      {eventTypeDistribution.map((e,i)=>(<Cell key={e.eventType} fill={COLORS[i % COLORS.length]} />))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div style={{ marginTop:'2rem' }}>
            <h3 style={{ marginBottom:'.5rem' }}>Event Search Results</h3>
            {!searchPage && <div style={{ fontSize:'.75rem', color:'#666' }}>Adjust filters and click Apply to search events.</div>}
            {(() => { const arr = searchPage?.content || searchPage?.items || []; return (searchPage && arr.length === 0); })() && <div className="empty-state">No events for filters.</div>}
            {(() => { const arr = searchPage?.content || searchPage?.items || []; return (searchPage && arr.length > 0); })() && (
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Type</th>
                      <th>Session</th>
                      <th>Correlation</th>
                      <th>Payload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(searchPage?.content || searchPage?.items || []).map(ev => (
                      <tr key={ev.eventId || ev.id}>
                        <td>{new Date(ev.occurredAt || ev.createdAt).toLocaleString()}</td>
                        <td>{ev.userId || filters.userId}</td>
                        <td>{ev.eventType}</td>
                        <td title={ev.sessionId}>{(ev.sessionId||'').slice(0,8)}</td>
                        <td title={ev.correlationId}>
                          {ev.correlationId ? (
                            <Link 
                              to={`/settings/correlation/${ev.correlationId}`}
                              style={{ color:'var(--primary)', textDecoration:'underline' }}
                            >
                              {ev.correlationId.slice(0,8)}
                            </Link>
                          ) : '—'}
                        </td>
                        <td style={{ maxWidth:340 }}>{renderPayloadTokens(ev.payloadJson)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {searchPage && (
              <div style={{ marginTop:'0.75rem', display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem' }}>
                <div style={{ fontSize:'.7rem', color:'#555' }}>Page {(searchPage.page ?? filters.page ?? 0)+1} of {searchPage.totalPages || 1} | Total: {searchPage.totalElements?.toLocaleString?.()}</div>
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button className="secondary" disabled={(filters.page||0)===0} onClick={()=>handlePageChange(-1)}>Prev</button>
                  <button className="secondary" disabled={searchPage?.last} onClick={()=>handlePageChange(1)}>Next</button>
                </div>
              </div>
            )}
          </div>

        </>
      )}
      
      {/* Active Sessions - visible for admin regardless of userId filter */}
      {user?.roles?.some(r=>r.roleName==='admin') && activeSessions.length > 0 && (
        <div style={{ marginTop:'2rem' }}>
          <h3 style={{ marginBottom:'.5rem' }}>Active Sessions</h3>
          <ul style={{ listStyle:'none', padding:0, margin:0, display:'grid', gap:'0.5rem' }}>
            {activeSessions.map(s => (
              <li key={s.sessionId} style={{ background:'var(--card-bg)', border:'1px solid var(--border-light)', borderRadius:'8px', padding:'0.5rem 0.75rem', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'1rem' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem', flex:1 }}>
                  <div style={{ fontSize:'.75rem', fontWeight:600, fontFamily:'monospace' }}>{s.sessionId}</div>
                  <div style={{ fontSize:'.65rem', color:'var(--text-muted)' }}>Events: {s.eventCount} • First: {s.firstEventAt ? new Date(s.firstEventAt).toLocaleTimeString() : '—'} • Last: {s.lastEventAt ? new Date(s.lastEventAt).toLocaleTimeString() : '—'}</div>
                </div>
                <Link 
                  to={`/settings/session-journey/${s.sessionId}`}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    background: 'var(--primary)', 
                    color: '#fff', 
                    borderRadius: '6px', 
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  View Journey →
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Landing Page Sessions - anonymous visitors (admin only) */}
      {user?.roles?.some(r=>r.roleName==='admin') && landingPageSessions.length > 0 && (
        <div style={{ marginTop:'2rem' }}>
          <h3 style={{ marginBottom:'.5rem' }}>Landing Page Sessions</h3>
          <p style={{ fontSize:'.7rem', color:'var(--text-muted)', marginBottom:'0.75rem' }}>Anonymous visitor sessions (no userId) — useful for tracking conversion funnels from ads and web searches</p>
          <ul style={{ listStyle:'none', padding:0, margin:0, display:'grid', gap:'0.5rem' }}>
            {landingPageSessions.map(s => {
              // Extract UTM parameters from first event payload if available
              let utmInfo = null;
              try {
                // Note: Backend doesn't return payloads in active-sessions endpoint by default
                // This will be enhanced when we add payload support or fetch first event separately
                utmInfo = s.utmSource || s.utmMedium || s.utmCampaign ? {
                  source: s.utmSource,
                  medium: s.utmMedium,
                  campaign: s.utmCampaign,
                  referrer: s.referrer
                } : null;
              } catch {}
              
              return (
                <li key={s.sessionId} style={{ background:'var(--card-bg)', border:'1px solid var(--border-light)', borderRadius:'8px', padding:'0.5rem 0.75rem', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'1rem' }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem', flex:1 }}>
                    <div style={{ fontSize:'.75rem', fontWeight:600, fontFamily:'monospace' }}>{s.sessionId}</div>
                    <div style={{ fontSize:'.65rem', color:'var(--text-muted)' }}>
                      Events: {s.eventCount} • First: {s.firstEventAt ? new Date(s.firstEventAt).toLocaleTimeString() : '—'} • Last: {s.lastEventAt ? new Date(s.lastEventAt).toLocaleTimeString() : '—'}
                    </div>
                    {utmInfo && (
                      <div style={{ fontSize:'.6rem', color:'var(--text-muted)', fontStyle:'italic' }}>
                        {utmInfo.source && `Source: ${utmInfo.source}`}
                        {utmInfo.medium && ` • Medium: ${utmInfo.medium}`}
                        {utmInfo.campaign && ` • Campaign: ${utmInfo.campaign}`}
                        {utmInfo.referrer && ` • Referrer: ${utmInfo.referrer}`}
                      </div>
                    )}
                  </div>
                  <Link 
                    to={`/settings/session-journey/${s.sessionId}`}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      background: 'var(--primary)', 
                      color: '#fff', 
                      borderRadius: '6px', 
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    View Journey →
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      
      {/* Recent user activity panel (admin only) visible regardless of userId filter */}
      {user?.roles?.some(r=>r.roleName==='admin') && (
        <div style={{ marginTop:'2rem' }}>
          <h3 style={{ marginBottom:'.5rem' }}>Recent User Activity</h3>
          {recentUsersLoading && <div style={{ fontSize:'.7rem', color:'#666' }}>Loading recent activity…</div>}
          {recentUsersError && <div style={{ fontSize:'.7rem', color:'#c00' }}>{recentUsersError.message}</div>}
          {!recentUsersLoading && recentUsersPage && recentUsersPage.content?.length === 0 && <div className="empty-state">No recent events.</div>}
          {!recentUsersLoading && recentUsersPage && recentUsersPage.content?.length > 0 && (
            <div style={{ overflowX:'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Event Type</th>
                    <th>Occurred</th>
                    <th>Session</th>
                    <th>Correlation</th>
                    <th>Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsersPage.content.map(ev => {
                    const handleRowClick = () => {
                      const params = new URLSearchParams();
                      if (ev.eventType) params.set('eventType', ev.eventType);
                      if (ev.sessionId) params.set('sessionId', ev.sessionId);
                      if (ev.correlationId) params.set('correlationId', ev.correlationId);
                      // Wide date range (1 year) for context
                      const now = new Date();
                      const from = new Date(now.getTime() - 365*86400000);
                      params.set('from', from.toISOString());
                      params.set('to', now.toISOString());
                      navigate(`/settings/usage-events/user/${ev.userId}?${params.toString()}`);
                    };
                    return (
                      <tr key={ev.eventId || ev.id} onClick={handleRowClick} style={{ cursor:'pointer' }}>
                        <td>{ev.userId}</td>
                        <td>{[ev.firstName, ev.lastName].filter(Boolean).join(' ') || '—'}</td>
                        <td>{ev.email || '—'}</td>
                        <td>{ev.eventType}</td>
                        <td>{new Date(ev.occurredAt || ev.createdAt).toLocaleString()}</td>
                        <td title={ev.sessionId}>{(ev.sessionId||'').slice(0,8)}</td>
                        <td title={ev.correlationId} onClick={(e) => e.stopPropagation()}>
                          {ev.correlationId ? (
                            <Link 
                              to={`/settings/correlation/${ev.correlationId}`}
                              style={{ color:'var(--primary)', textDecoration:'underline' }}
                            >
                              {ev.correlationId.slice(0,8)}
                            </Link>
                          ) : '—'}
                        </td>
                        <td style={{ maxWidth:340 }}>{renderPayloadTokens(ev.payloadJson)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {recentUsersPage && (
            <div style={{ marginTop:'0.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
              <div style={{ fontSize:'.65rem', color:'#555' }}>Page {(recentUsersPage.page ?? recentUsersPage.number ?? recentUsersPage.numberOfElements ?? 0) + 1} of {recentUsersPage.totalPages || 1}</div>
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button className="secondary" disabled={recentUsersPage.first} onClick={recentUsersPrev}>Prev</button>
                <button className="secondary" disabled={recentUsersPage.last} onClick={recentUsersNext}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background:'var(--card-bg)', border:'1px solid var(--border-light)', borderRadius:'12px', padding:'0.75rem 1rem', display:'flex', flexDirection:'column', gap:'0.25rem', boxShadow:'var(--shadow-sm)' }}>
      <div style={{ fontSize:'.65rem', fontWeight:600, letterSpacing:'0.05em', textTransform:'uppercase', color:'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize:'1.1rem', fontWeight:600 }}>{value}</div>
    </div>
  );
}
